import React from "react";
import ZStack from "@/components/layout/ZStack";
import Image from "@/components/media/Image";
import Visibility from "@/components/shared/Visibility";
import { breakpointClasses, cn, splitBreakpointProps } from "@/lib/utils";
import type { BackgroundImageProps } from "./types";

function BackgroundImage<E extends React.ElementType = "div">({
  id,
  as,
  src,
  alt,
  width,
  height,
  fill = true,
  sizes = "100vw",
  overlay,
  film,
  foreground,
  showImage = true,
  children,
  className,
  imageClassName,
  style = {},
  ...props
}: BackgroundImageProps<E>) {
  const { bp, native } = splitBreakpointProps(props);
  const classes = breakpointClasses(bp);

  return (
    <ZStack
      as={as as any}
      id={id ?? "background-image"}
      className={cn("relative w-full h-full", className)}
      style={style}
    >
      <Visibility shouldShow={showImage}>
        <ZStack.Child zIndex={0}>
          <Image
            src={src}
            alt={alt}
            width={width}
            height={height}
            fill={fill}
            sizes={sizes}
            className={cn(imageClassName, classes)}
            {...native}
          />
        </ZStack.Child>
      </Visibility>

      <Visibility shouldShow={!!film}>
        <ZStack.Child
          id="image-film"
          zIndex={10}
          className={cn("absolute inset-0 pointer-events-none", film)}
        />
      </Visibility>

      <Visibility shouldShow={!!overlay}>
        <ZStack.Child
          id="img-overlay"
          zIndex={5}
          className={cn("absolute inset-0 pointer-event-none", overlay)}
        />
      </Visibility>

      <ZStack.Child
        id="img-foreground-content"
        zIndex={15}
        className={cn("relative h-full w-full", foreground)}
      >
        {children}
      </ZStack.Child>
    </ZStack>
  );
}

export default BackgroundImage;
